import { clamp, getCropRect, handleGrabPx } from './geometry';
import { MAX_SCALE, MIN_SCALE, activeSet, state, type Side } from './state';

export interface InputHooks {
  /** Repaint the live canvas from `state`. */
  draw(): void;
  /** Called with the new divider x on every user-driven divider move. */
  onSliderMove(x: number): void;
  /** Called when a pinch changes a side's scale (so the zoom slider can follow). */
  onScaleChanged(side: Side, scale: number): void;
}

interface Point {
  x: number;
  y: number;
}

type DragTarget = 'handle' | Side;

/**
 * Mouse + touch handling for the compare canvas. Always acts on the
 * currently *active* photo set. Mirrors the prototype:
 *  - near the divider (or while any recording is active) → drag the divider
 *  - otherwise the half under the pointer decides which photo pans
 *  - two-finger touch on a half → pinch-zoom that photo only
 * Touch is handled separately from mouse so pinch and one-finger drag don't
 * fight, and so page scroll can be blocked mid-gesture.
 */
export function attachInput(canvas: HTMLCanvasElement, hooks: InputHooks): void {
  let dragging = false;
  let dragTarget: DragTarget | null = null;
  let lastPos: Point | null = null;
  let pinching = false;
  let pinchKey: Side | null = null;
  let pinchStartDist = 0;
  let pinchStartScale = 1;

  function posFromEvent(e: MouseEvent | TouchEvent): Point {
    const rect = canvas.getBoundingClientRect();
    const touch = 'touches' in e ? e.touches[0] : undefined;
    const clientX = touch ? touch.clientX : (e as MouseEvent).clientX;
    const clientY = touch ? touch.clientY : (e as MouseEvent).clientY;
    const x = (clientX - rect.left) * (canvas.width / rect.width);
    const y = (clientY - rect.top) * (canvas.height / rect.height);
    return { x: clamp(x, 0, canvas.width), y: clamp(y, 0, canvas.height) };
  }

  function getTouchDist(touches: TouchList): number {
    const a = touches[0]!;
    const b = touches[1]!;
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function keyForX(x: number): Side {
    return x < state.sliderX ? 'A' : 'B';
  }

  function moveSlider(x: number): void {
    state.sliderX = x;
    hooks.onSliderMove(x);
    hooks.draw();
  }

  function pointerDown(e: MouseEvent | TouchEvent): void {
    if (state.autoAnimating) return;
    const p = posFromEvent(e);
    const grabsHandle =
      state.recording || state.pathRecording || Math.abs(p.x - state.sliderX) < handleGrabPx(canvas.width);
    if (grabsHandle) {
      dragTarget = 'handle';
      dragging = true;
      moveSlider(p.x);
      return;
    }
    const key = keyForX(p.x);
    if (activeSet().locked[key]) return;
    dragTarget = key;
    dragging = true;
    lastPos = p;
  }

  function pointerMove(e: MouseEvent | TouchEvent): void {
    if (!dragging) return;
    const p = posFromEvent(e);
    if (dragTarget === 'handle') {
      moveSlider(p.x);
    } else if (dragTarget && lastPos) {
      const img = activeSet().images[dragTarget];
      if (!img) return;
      const t = activeSet().transforms[dragTarget];
      const { sw, sh } = getCropRect(img.bitmap, canvas.width, canvas.height, t);
      const dx = p.x - lastPos.x;
      const dy = p.y - lastPos.y;
      // convert canvas-px motion to source-px motion; image follows the finger
      t.offsetX -= dx * (sw / canvas.width);
      t.offsetY -= dy * (sh / canvas.height);
      lastPos = p;
      hooks.draw();
    }
  }

  function pointerUp(): void {
    dragging = false;
    lastPos = null;
    dragTarget = null;
  }

  // mouse (desktop)
  canvas.addEventListener('mousedown', pointerDown);
  window.addEventListener('mousemove', pointerMove);
  window.addEventListener('mouseup', pointerUp);

  // touch (mobile)
  canvas.addEventListener(
    'touchstart',
    (e) => {
      if (state.autoAnimating) return;
      if (!state.recording && !state.pathRecording && e.touches.length === 2) {
        e.preventDefault();
        dragging = false;
        const rect = canvas.getBoundingClientRect();
        const t0 = e.touches[0]!;
        const t1 = e.touches[1]!;
        const midX = ((t0.clientX + t1.clientX) / 2 - rect.left) * (canvas.width / rect.width);
        const key = keyForX(midX);
        if (activeSet().locked[key]) {
          pinching = false;
          pinchKey = null;
          return;
        }
        pinching = true;
        pinchKey = key;
        pinchStartDist = getTouchDist(e.touches);
        pinchStartScale = activeSet().transforms[key].scale;
        return;
      }
      e.preventDefault();
      pointerDown(e);
    },
    { passive: false },
  );

  window.addEventListener(
    'touchmove',
    (e) => {
      if (pinching && pinchKey && e.touches.length === 2) {
        e.preventDefault();
        const dist = getTouchDist(e.touches);
        const newScale = clamp(pinchStartScale * (dist / pinchStartDist), MIN_SCALE, MAX_SCALE);
        activeSet().transforms[pinchKey].scale = newScale;
        hooks.onScaleChanged(pinchKey, newScale);
        hooks.draw();
        return;
      }
      if (dragging) {
        e.preventDefault();
        pointerMove(e);
      }
    },
    { passive: false },
  );

  window.addEventListener('touchend', (e) => {
    if (e.touches.length < 2) {
      pinching = false;
      pinchKey = null;
    }
    if (e.touches.length === 0) pointerUp();
  });
  window.addEventListener('touchcancel', () => {
    pinching = false;
    pinchKey = null;
    pointerUp();
  });
}
